<template>
  <div class="pn-page py-3">
    <header class="pn-toolbar">
      <div class="pn-toolbar__title">
        <span class="pn-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Private Notes</h1>
          <p>Your personal notebook. Only you can read these — body is encrypted at rest.</p>
        </div>
      </div>
      <div class="pn-toolbar__actions">
        <button type="button" class="pn-btn pn-btn--primary" @click="openCreate">+ New Note</button>
        <button
          type="button"
          class="pn-btn pn-btn--ghost"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadNotes"
        >{{ isLoading ? 'Refreshing…' : 'Refresh' }}</button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="pn-alert">{{ errorMessage }}</CAlert>

    <section class="pn-section">
      <div class="pn-section__body">
        <div class="pn-filters">
          <div class="pn-filter">
            <label class="pn-label">Search title</label>
            <AppInput
              no-margin
              :value="searchTerm"
              placeholder="Type to filter your notes"
              @update:value="searchTerm = $event"
            />
          </div>
        </div>
      </div>
    </section>

    <div v-if="isLoading && !items.length" class="pn-empty">Loading…</div>
    <div v-else-if="!filteredNotes.length" class="pn-empty">
      {{ items.length ? 'No notes match that search.' : 'You haven’t written any private notes yet.' }}
    </div>
    <div v-else class="pn-grid">
      <article
        v-for="note in filteredNotes"
        :key="note.id"
        :class="['pn-card', `pn-card--${note.color || 'default'}`, { 'is-pinned': note.pinned }]"
        @click="openEdit(note)"
      >
        <header class="pn-card__head">
          <h3>{{ note.title }}</h3>
          <span v-if="note.pinned" class="pn-pin" title="Pinned">★</span>
        </header>
        <p class="pn-card__body">{{ truncatedBody(note.body) }}</p>
        <footer class="pn-card__foot">
          <span>{{ formatDate(note.updated_at) }}</span>
          <button
            type="button"
            class="pn-icon-btn"
            title="Delete"
            @click.stop="confirmDelete(note)"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            </svg>
          </button>
        </footer>
      </article>
    </div>

    <CModal
      :show.sync="showModal"
      size="lg"
      :centered="true"
      :title="form.id ? 'Edit Note' : 'New Note'"
      class="pn-modal"
      :close-on-backdrop="!isSaving"
    >
      <div class="pn-modal__body">
        <div v-if="modalError" class="pn-modal__alert">{{ modalError }}</div>
        <div class="pn-modal__field">
          <AppInput
            no-margin
            label="Title"
            required
            :value="form.title"
            :disabled="isSaving"
            placeholder="What's this note about?"
            @update:value="form.title = $event"
          />
        </div>
        <div class="pn-modal__field">
          <label class="pn-label">Body</label>
          <textarea
            v-model="form.body"
            class="pn-textarea"
            rows="10"
            :disabled="isSaving"
            placeholder="Write your thoughts here…"
          ></textarea>
        </div>
        <div class="pn-modal__field-row">
          <div>
            <label class="pn-label">Colour</label>
            <div class="pn-color-row">
              <button
                v-for="c in colors"
                :key="c"
                type="button"
                :class="['pn-color-chip', `pn-card--${c}`, { 'is-active': form.color === c }]"
                :title="c"
                :disabled="isSaving"
                @click="form.color = c"
              ></button>
            </div>
          </div>
          <label class="pn-toggle">
            <input type="checkbox" v-model="form.pinned" :disabled="isSaving" />
            <span>Pin to the top</span>
          </label>
        </div>
      </div>
      <template #footer>
        <button
          type="button"
          class="pn-btn pn-btn--ghost"
          :disabled="isSaving"
          @click="closeModal"
        >Cancel</button>
        <button
          type="button"
          class="pn-btn pn-btn--primary"
          :disabled="isSaving || !isFormValid"
          @click="submit"
        >{{ isSaving ? 'Saving…' : 'Save' }}</button>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="deleteDialogOpen"
      title="Delete note?"
      :message="deleteMessage"
      confirm-label="Delete"
      variant="danger"
      :busy="isDeleting"
      @confirm="executeDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'

const DEFAULT_COLORS = ['default', 'green', 'yellow', 'blue', 'pink', 'gray']

export default {
  name: 'PrivateNotes',
  components: { AppInput, AppConfirmDialog },
  data() {
    return {
      items: [],
      colors: DEFAULT_COLORS,
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      showModal: false,
      modalError: '',
      isSaving: false,
      form: this.emptyForm(),
      deleteDialogOpen: false,
      pendingDelete: null,
      isDeleting: false,
    }
  },
  computed: {
    filteredNotes() {
      const term = (this.searchTerm || '').trim().toLowerCase()
      if (!term) {
        return this.items
      }
      return this.items.filter((n) => (n.title || '').toLowerCase().includes(term))
    },
    isFormValid() {
      return Boolean(this.form.title && this.form.title.trim())
    },
    deleteMessage() {
      if (!this.pendingDelete) {
        return ''
      }
      return `“${this.pendingDelete.title}” will be permanently deleted.`
    },
  },
  methods: {
    emptyForm() {
      return { id: null, title: '', body: '', color: 'default', pinned: false }
    },
    truncatedBody(body) {
      if (!body) {
        return ''
      }
      const clean = body.replace(/\s+/g, ' ').trim()
      return clean.length > 220 ? `${clean.slice(0, 220)}…` : clean
    },
    formatDate(value) {
      if (!value) {
        return ''
      }
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) {
        return value
      }
      return d.toLocaleString('en-IN', {
        day:   '2-digit',
        month: 'short',
        year:  'numeric',
        hour:  '2-digit',
        minute: '2-digit',
      })
    },
    openCreate() {
      this.form = this.emptyForm()
      this.modalError = ''
      this.showModal = true
    },
    openEdit(note) {
      this.form = {
        id:     note.id,
        title:  note.title || '',
        body:   note.body || '',
        color:  note.color || 'default',
        pinned: Boolean(note.pinned),
      }
      this.modalError = ''
      this.showModal = true
    },
    closeModal() {
      if (this.isSaving) {
        return
      }
      this.showModal = false
      this.form = this.emptyForm()
      this.modalError = ''
    },
    submit() {
      if (!this.isFormValid) {
        return
      }
      const payload = {
        title:  this.form.title.trim(),
        body:   this.form.body || null,
        color:  this.form.color || 'default',
        pinned: Boolean(this.form.pinned),
      }
      const self = this
      this.isSaving = true
      this.modalError = ''

      const request = this.form.id
        ? axios.put(`${this.$apiAdress}/api/private-notes/${this.form.id}`, payload)
        : axios.post(`${this.$apiAdress}/api/private-notes`, payload)

      request
        .then(function () {
          self.showModal = false
          self.form = self.emptyForm()
          self.loadNotes()
        })
        .catch(function (error) {
          self.modalError = self.extractError(error, 'Could not save note.')
        })
        .finally(function () {
          self.isSaving = false
        })
    },
    confirmDelete(note) {
      this.pendingDelete = note
      this.deleteDialogOpen = true
    },
    cancelDelete() {
      this.deleteDialogOpen = false
      this.pendingDelete = null
    },
    executeDelete() {
      if (!this.pendingDelete) {
        return
      }
      const id = this.pendingDelete.id
      const self = this
      this.isDeleting = true
      axios.delete(`${this.$apiAdress}/api/private-notes/${id}`)
        .then(function () {
          self.items = self.items.filter((row) => row.id !== id)
          self.cancelDelete()
        })
        .catch(function (error) {
          self.errorMessage = self.extractError(error, 'Could not delete note.')
        })
        .finally(function () {
          self.isDeleting = false
        })
    },
    loadNotes() {
      const self = this
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/private-notes`)
        .then(function (response) {
          const payload = response.data || {}
          self.items = Array.isArray(payload.data) ? payload.data : []
          if (Array.isArray(payload.colors) && payload.colors.length) {
            self.colors = payload.colors
          }
        })
        .catch(function (error) {
          self.errorMessage = self.extractError(error, 'Could not load notes.')
          self.items = []
        })
        .finally(function () {
          self.isLoading = false
        })
    },
    extractError(error, fallback) {
      const data = error && error.response && error.response.data
      if (data && data.message) {
        return data.message
      }
      if (data && data.error && typeof data.error === 'object') {
        const first = Object.values(data.error)[0]
        if (Array.isArray(first) && first.length) {
          return first[0]
        }
      }
      return fallback
    },
  },
  mounted() {
    this.loadNotes()
  },
}
</script>

<style scoped>
.pn-page {
  padding-left: 1rem;
  padding-right: 1rem;
}

.pn-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.pn-toolbar__title {
  display: flex;
  gap: 0.85rem;
  align-items: stretch;
}

.pn-toolbar__accent {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.pn-toolbar__title h1 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.pn-toolbar__title p {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0.2rem 0 0;
}

.pn-toolbar__actions {
  display: inline-flex;
  gap: 0.55rem;
}

.pn-alert {
  border-radius: 10px;
  font-size: 0.85rem;
}

.pn-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.pn-section__body {
  padding: 0.85rem 1rem;
}

.pn-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.pn-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 280px;
  min-width: 0;
}

.pn-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  margin-bottom: 0.3rem;
}

.pn-empty {
  background: #ffffff;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  padding: 1.6rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
}

.pn-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.85rem;
}

.pn-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 0.85rem 0.95rem 0.7rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  min-height: 150px;
}

.pn-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  border-color: #d8dde6;
}

.pn-card.is-pinned {
  border-color: #f59e0b;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.18);
}

.pn-card--default { background: #ffffff; }
.pn-card--green   { background: #f1faf4; border-color: #d2ebdc; }
.pn-card--yellow  { background: #fff8e6; border-color: #f3e1ad; }
.pn-card--blue    { background: #eef4ff; border-color: #d3deef; }
.pn-card--pink    { background: #fdf2f5; border-color: #ecd0d8; }
.pn-card--gray    { background: #f6f7f9; border-color: #dfe3eb; }

.pn-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.pn-card__head h3 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

.pn-pin {
  color: #f59e0b;
  font-size: 1rem;
  line-height: 1;
}

.pn-card__body {
  flex: 1 1 auto;
  font-size: 0.82rem;
  color: #4f5560;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
}

.pn-card__foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.7rem;
  font-size: 0.72rem;
  color: #6b7280;
}

.pn-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.pn-icon-btn:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.pn-icon-btn:focus { outline: none; }
.pn-icon-btn::-moz-focus-inner { border: 0; }
.pn-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.30);
}

.pn-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.pn-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.pn-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.pn-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.pn-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.pn-modal__body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.pn-modal__field { display: flex; flex-direction: column; }

.pn-modal__field-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}

.pn-modal__alert {
  padding: 0.6rem 0.85rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.pn-textarea {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  padding: 0.6rem 0.7rem;
  font-size: 0.88rem;
  color: #1f2a44;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  resize: vertical;
}

.pn-textarea:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pn-color-row {
  display: inline-flex;
  gap: 0.4rem;
  margin-top: 0.1rem;
}

.pn-color-chip {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #d8dde6;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.pn-color-chip:hover {
  transform: translateY(-1px);
}

.pn-color-chip.is-active {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #16a07a;
}

.pn-color-chip:focus { outline: none; }
.pn-color-chip::-moz-focus-inner { border: 0; }
.pn-color-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.32);
}

.pn-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #1f2a44;
  cursor: pointer;
  margin: 0;
}

.pn-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 96px;
  padding: 0 1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.pn-btn:focus { outline: none; }
.pn-btn::-moz-focus-inner { border: 0; }
.pn-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.pn-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.pn-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.pn-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.pn-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.pn-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}
</style>
